<template lang="pug">
PagePeriod(
  :bannerImage="require(`./Postmodernisme_01.jpg`)"
  :circleImage="require(`./sirkel_postmodernisme.png`)"
  )
  // Content here

  h3 Tida og samfunnstilhøva

  figure
    img(src="./Postmodernisme_Tida_og_samfunnsforhold1.jpg" alt="Mann i dress fyller bilen med bensin frå ei kanne. Foto.")
    figcaption I 1970 kunne industriminister Sverre Walter Rostoft helle den første norskproduserte bensinen på bilen sin. Foto: NTB / Scanpix

  h5 Velferdsstat og oljeøkonomi
  p I 1960-åra hadde dei fleste landa i Europa reist seg etter andre verdskrigen. Parallelt med ein stadig aukande velstand vart det skapt eit velferdssamfunn. Lova om folketrygd kom i 1967 og bana veg for at alle borgarane fekk rett til alderspensjon og økonomisk hjelp ved for eksempel sjukdom, arbeidsløyse, svangerskap og fødsel. 

  p I 1969 vart det funne olje i Nordsjøen. Inntektene frå oljeindustrien er den viktigaste årsaka til at Noreg stort sett har hatt ein stabilt god økonomi dei siste førti åra.


  h5 Ungdoms- og hippiekultur
  p Sidan 1950-åra hadde ungdomskulturen vore prega av opprør og motstand mot eit materialistisk og konformt vaksenliv. Mot slutten av 60-åra utvikla ungdomsopprøret seg til å bli eit internasjonalt politisk opprør og ein generell kamp for frigjering og antiautoritære verdiar. 

  p I mai 1968 kom det til kraftige samanstøytar mellom studentar og politi i Paris. Opprøret utvikla seg raskt til å bli ei politisk massemønstring, som har gitt namn til ein generasjon radikale studentar over heile den vestlege verda – dei såkalla sekstiåttarane. 

  p I USA voks 
    em hippiekulturen 
    | fram i 1960-åra. 
    em Hippie 
    | kjem av det engelske ordet ”hip”, eit slanguttrykk for ”moderne og frigjort menneske”. Rockemusikk, alternativ livsstil og rusmiddel var sentrale element i hippieprotesten mot materialistiske verdiar og mot USAs krigføring i Vietnam.

  figure
    img(src="./Postmodernisme_Tida_og_samfunnsforhold2.jpg" alt="Fem personar med mye hår, store frakkar og slengbukser poserer foran eit utbrend hus. Foto.")
    figcaption Rockebandet Steppenwolf, kjend for låta «Born to Be Wild». Foto: Henry Diltz / Corbis / Scanpix

  figure.col.col5.right-image
    img(src="./Postmodernisme_Demonstrasjon-mot-EEC.jpg" alt="Fredeleg demonstrasjonstog mot EEC på Karl Johan med Slottet i bakgrunnen.Foto.")
    figcaption Demonstrasjon mot EEC i 1972. Foto: Jan A. Martinsen / Aftenposten / Scanpix

  h5 EU-kamp i Noreg
  p Både i 1972 og i 1994 vart det halde folkerøysting om EU-medlemskap i Noreg, og begge gongene røysta eit knapt fleirtal mot medlemskap i unionen. Gjennom den såkalla EØS-avtalen er norsk økonomi og handel i dag likevel sterkt påverka av lovverket og politikken i EU-landa.

  h5 Kald krig og eit delt Europa
  p Tilhøvet mellom Sovjetunionen og USA utvikla seg etter andre verdskrigen til å bli svært spent. Denne ”kalde krigen” påverka heile verdssamfunnet. Europa var delt i to – dei vesteuropeiske landa var stort sett USAs allierte, mens dei austeuropeiske landa var i allianse med Sovjetunionen. 

  figure.col.col7.right-image
    img(src="./Postmodernisme_Tida_og_samfunnsforhold4.jpg" alt="To personar som tittar over ein mur med piggtråd. Foto.")
    figcaption Eit par i Vest-Berlin talar med slektningar i Aust-Berlin. Foto: Bettmann/Corbis/Scanpix

  p Grensene mellom austblokk- og vestblokklanda var så godt som stengde, særleg for austeuropeiske borgarar, og levestandarden i Aust-Europa var mykje lågare enn i dei vesteuropeiske landa. 

  p Først mot slutten av 1980-åra endra situasjonen seg. I 1985 vart Mikhail Gorbatsjov (1931– ) president i Sovjetunionen. Han innførte såkalla perestrojka, økonomiske reformer, og glasnost, eit meir ope samfunn, og revolusjonerte styreforma i landet på berre få år. 

  h5 Austblokklanda frigjer seg 
  p Hausten 1989 skjedde det ein revolusjon i Aust-Europa som førte til at kommunistregima i dei ulike landa kollapsa etter tur. I november 1989 fall også muren mellom Aust- og Vest-Berlin, som hadde delt byen i to heilt sidan 1961. I 1990 vart Vest- og Aust-Tyskland samla på nytt, og i 1991 gjekk Sovjetunionen i oppløysing. 

  h5 Bosnia-krigen 
  p I dei fleste austblokklanda gjekk endringsprosessane i 1990-åra fredeleg for seg, men i det tidlegare Jugoslavia auka spenningane mellom dei ulike folkegruppene. Den verste konflikten braut ut i Bosnia-Hercegovina, der over hundre tusen menneske vart drepne. Krigen gjekk hardast ut over den muslimske delen av innbyggjarane. 

  figure.col.col4.left-image
    img(src="./Postmodernisme_Fane-til-kvinnedagen.jpg" alt="To kvinner held opp eit banner der det står «lønna arbeid til alle kvinner. Husarbeid til alle menn». Foto.")
    figcaption Fane til Kvinnedagen i 1982. Foto: Aftenposten/Scanpix

  h5 Jamstillingskamp 
  p 1950-åra var prega av konservative familieverdiar: Far jobba og tok del i samfunnslivet, mor passa heimen og barna. Utover i 60- og 70-åra voks det fram ei sterk kvinnerørsle både i USA og i Vest-Europa, med krav om jamstilling mellom kjønna. Mange rettar som kvinnene kjempa for den gongen, er i dag sjølvsagde for kvinner i vestlege land.

  h5 Miljøkamp
  p I 1970-åra engasjerte stadig fleire seg i miljøspørsmål. Mange var kritiske til eit høgt forbruk og frykta konsekvensane det kunne få for miljø og menneske. Særleg skapte bygginga av atomkraftverk sterk motstand, og i fleire sentraleuropeiske land vart det skipa ”grøne parti”. 

  p I 1980-åra kom avskoging og utslepp av klimagassar på den miljøpolitiske dagsordenen. Utfordringane har ikkje vorte mindre på 2000-talet. Klimaendringane vi ser i dag, er mest sannsynleg menneskeskapte, men arbeidet med å redusere CO2-utsleppa går likevel seint. 

  figure.col.col6.right-image
    img(src="./Postmodernisme_Tida_og_samfunnsforhold6.jpg" alt="To skyskraparar med røyk og eksplosjon. Foto.")
    figcaption Åtaket på World Trade Center 11. september 2001. Foto: Carmen Taylor / AP / Scanpix

  h5 Kampen mot terror 
  p 11. september 2001 står som eit vegskilje i moderne internasjonal politikk og samfunnsliv. Mest tre tusen menneske vart drepne i terroråtaket som ramma World Trade Center i New York og Pentagon, det amerikanske militære hovudkvarteret. Ansvarleg for aksjonen var Al-Qaida, ei ekstremistisk islamistgruppe under leiing av Osama bin Laden. 

  p USA reagerte på terroråtaket med å lansere krigen mot terrorisme, ein global kampanje for å aksjonere mot internasjonale terroristgrupper og terrornettverk. Men også i perioden etter 2001 har islamistiske grupper gjennomført fleire terroråtak, ikkje berre i vestlege land, men også i Afrika, Midtausten og Asia. 

  figure
    img(src="./Postmodernisme_Tida_og_samfunnsforhold7.jpg" alt="Syv glade ungdomar med ulik hudfarge poserer på rekkje. Foto.")
    figcaption Gode vener på tvers av livssyn, verdiar og levemåtar. Foto: Kerstin Mertens / Samfoto / Scanpix

  h5 Eit meir mangfaldig Noreg
  p I 1970 budde 50 000 innvandrarar og norskfødde med innvandrarforeldre i Noreg, i dag er det 552 000. Vi har lært å kjenne ulike livssyn, verdiar og levemåtar som både utfordrar den norske kulturen og gjer han rikare.

  h3 Tankar i tida

  figure
    img(src="./Postmodernisme_Tanker_i_tiden1.jpg" alt="Høghus i bybilete. Foto.")
    figcaption Aker Brygge i Oslo kan stå som symbol på postmoderne arkitektur i Noreg. Foto: Vegard Grøtt / NTB Scanpix

  h5 Postmodernitet
  p Omgrepet 
    em postmodernitet 
    | har sidan 1980-åra vore brukt som ei samnemning på den økonomiske og kulturelle stoda i dei vestlege landa i samtida. Ordet kan omsetjast med ”tida eller tenkinga etter moderniteten”. For å forstå omgrepet må vi derfor vite kva modernitet er for noko. 

  p 
    em Modernitet 
    | kallar vi epoken frå opplysningstida og fram til vår tid. Dei sentrale ideane i denne epoken har vore ”dei tre f-ane”: trua på fridom, framsteg og fornuft. Vitskapleg og teknologisk utvikling og politisk og personleg fridom skulle leggje grunnlaget for at menneska fekk eit stadig betre og meir lykkeleg liv. Med fornufta som rettesnor skulle det vere mogleg å komme fram til absolutte sanningar i både vitskap, politikk og samfunnsliv. 

  figure.col.col4.left-image
    img(src="./Postmodernisme_Tanker_i_tiden2.jpg" alt="Portrett av mann i kvit skjorte. Foto.")
    figcaption Jean-François Lyotard. Foto: Bracha L. Ettinger / Wikimedia Commons

  h5 Kritikk av moderniteten
  p Postmoderne tenkjarar er kritiske til desse ideane. I boka 
    em Den postmoderne tilstand: En rapport om kunnskap 
    | (1979) skriv den franske filosofen <strong>Jean-François Lyotard</strong> (1924–1998) at ”de store fortellingene er døde”: Dei overordna ideane som har prega utviklinga i Vesten dei siste tre–fire hundre åra, er ikkje lenger innlysande sanningar. Vitskapen har medverka til å utvikle atomvåpen og kjemiske våpen, og trass i at den teknologiske utviklinga og industrialiseringa har betra levekåra for mange menneske, har ho òg skapt store globale miljø- og fattigdomsproblem.

  h5 Det postsekulære samfunnet
  p Trua på fridom og fornuft i opplysningstida førte til ei kritisk gransking av alle slags autoritetar, også av kyrkja. Gjennom dei siste 200 åra har det skjedd ein sekulariseringsprosess der både enkeltmennesket, kulturen og samfunnet i stadig større grad har frigjort seg frå religiøse autoritetar og normer. 

  p Men er Gud død? I 2005 var det 6,7 millionar besøk under gudstenester i Den norske kyrkja. I 2010 fanst det 42 registrerte trussamfunn og 2 livssynssamfunn berre i Oslo og Akershus. Mange menneske trur, men kva trur vi på? 

  figure.col.col4.left-image
    img(src="./Postmodernisme_Jurgen-Habermas.jpg" alt="Portrett av smilande mann i dress. Foto.")
    figcaption Jürgen Habermas. Foto: Marit Hommedal / Scanpix

  p Den tyske filosofen og sosiologen <strong>Jürgen Habermas</strong> (1929– ) omtaler våre vestlege samfunn i dag som postsekulære. Han meiner at religionen har ein sentral plass, både i livet til enkeltmenneska og i internasjonal storpolitikk. Den dominerande konflikten internasjonalt er i dag knytt til radikal islam, og vi ser ei stadig aukande interesse for ulike religionar og livssyn. Menneska søkjer etter meining og samanheng i tilværet, og opnar opp for både Gud, englar og trua på eit liv etter døden.

  h5 Postmodernisme
  p Frå 1960-åra og utover ser vi nye tendensar og straumdrag innanfor kunst, arkitektur og litteratur som vi gjerne summerer opp i nemninga postmodernisme. Postmodernisme er altså ein estetisk teori, ei lære om kva kunsten er og vil, om tilhøvet mellom kunstverk og publikum og om tilhøvet mellom den kunstnarlege verda og kvardagslivet. 

  figure.col.col4.right-image
    img(src="./Postmodernisme_Campbells-Soup.jpg" alt="Eit par ser på vegg med mange like plakatar av Campell hermetikkboks. Foto.")
    figcaption Andy Warhol: «Campbell’s Soup». Foto: Nelson Almeida/AFP/Scanpix

  p Postmodernismen kan sjåast som eit oppgjer med krava til innhald og form i den modernistiske kunsten. Der høgmodernistane bevisst braut med tidlegare kunstformer og tok avstand frå massekulturen, gjer postmodernistane det stikk motsette: Dei kombinerer nytt og gammalt, høgkultur og massekultur, dei alluderer til kjende tekstar, ironiserer over både eigne og andre sine verk – og resultatet er ofte eit leikande og frodig uttrykk. 

  h3 Litteraturen 1960–1990

  .flex.portretter
    figure.m1rem
      img(style="width: 198px" src="./Postmodernisme_Mehren.jpg" alt="Portrett av mann med briller og halvlangt hår. Foto.")
      figcaption Stein Mehren
    figure.m1rem
      img(style="width: 198px" src="./Postmodernisme_Kolbein-Falkeid.jpg" alt="Portrett av snakkande, eldre mann med skjegg i kofte. Foto.")
      figcaption Kolbein Falkeid
    figure.m1rem
      img(style="width: 198px" src="./Postmodernisme_Georg-Johannesen.jpg" alt="Portrett av en mann grått hår og skjegg, tørkle i halsen. Foto. ")
      figcaption Georg Johannesen

  h5 1960-åra: Sentrallyrikk i fokus
  p I 1960 er tungetaledebatten død, det vil seie debatten om den modernistiske lyrikken. Det er lite kontroversielt å skrive dikt utan taktfast rytme eller enderim. Men framleis er det mange som skriv tradisjonell lyrikk, jamvel om den frie forma no dominerer. 

  p Ein lyrikar som debuterte i 1960, var <strong>Stein Mehren</strong> (1935–2017). Mehren skriv sentrallyriske dikt om død og kjærleik, og han skriv modernistisk om forholdet mellom språk og erkjenning. Mehrens poesi er intellektuell og filosofisk, med røter attende til romantikken, men med eit blikk fram mot moderniteten og postmoderniteten – som han ofte er veldig kritisk til. 

  p <strong>Kolbein Falkeid</strong> (1933–2021) debuterte i 1962. Falkeid er kjend for å skrive ein kombinasjon av eksistensiell sentrallyrikk og kvardagspoesi, og han er ein folkekjær og mykje lesen poet. Samarbeidet med gruppa Vamp har òg ført til at tekstane hans er formidla til mange. 

  NrkVideo(id="3284")

  p Ein lyrikar som vanskeleg lèt seg plassere innanfor ei retning, er <a href="https://www.nrk.no/kultur/georg-johannesen-om-kassandra-1.6849754" target="_blank"><strong>Georg Johannesen</strong></a> (1931–2005). Han debuterte i slutten av 1950-åra, men var med på å prege norsk litteratur og norsk litterær debatt i fleire tiår. Som politisk radikalar engasjerte han seg sterkt gjennom både dikt, essay, romanar og sakprosatekstar – ofte med eit kritisk blikk på Noreg og nordmenn.

  h5 Kjøpt: ja – lesen: tja 
  p I 1965 vart 
    em innkjøpsordninga for nyskriven norsk skjønnlitteratur 
    | etablert. Ordninga er ein føresetnad for det mangfaldet vi etter kvart får av diktsamlingar i Noreg. Stortinget gjer vedtak om at 1000 eksemplar av dei fleste norske skjønnlitterære bøkene, prosa, drama, lyrikk og gjendiktingar av lyrikk, skal kjøpast inn av staten. Norsk kulturråd administrerer ordninga, og bøkene blir sende ut til folkebiblioteka rundt om i landet. 

  figure.col.col6.right-image
    img(src="./Postmodernisme_Litteraturen4.jpg" alt="Tre menn i samtale rundt stovebordet. Foto.")
    figcaption «Dei tre unge opprørarane frå gruppa kring det litterære tidsskriftet Profil» var teksten til dette biletet då det sto på trykk i 1966. Tor Obrestad, Paal-Helge Haugen og Einar Økland.  Foto: NTB / Scanpix

  h5 Profil-gruppa
  p I 1965 samla ei gruppe venstreorienterte unge studentar, skribentar, forfattarar og intellektuelle seg og gav ut litteraturtidsskriftet Profil. Profil-forfattarane eksperimenterte med både prosa og lyrikk, og dei lanserte ei ny form for modernisme – 
    em Profil-modernismen 
    | eller 
    em nyenkel 
    | dikting. 

  p Profil-lyrikarane henta inspirasjon frå mellom anna austleg haikudikting og skreiv dikt med kvardagslege og konkrete motiv. Språkleg lét dei seg inspirere av kvardagsspråk, munnleg sjargong, poptekstar og reklamespråk. Kjende Profil-lyrikarar er <strong>Jan Erik Vold</strong> (1939– ), <strong>Eldrid Lunden</strong> (1940– ), <strong>Einar Økland</strong> (1940– ) og <strong>Paal Helge Haugen</strong> (1945– ).

  .flex.portretter.portretter-4
    figure.m1rem
      img(src="./Postmodernisme_Jan-Erik-Vold.jpg" alt="Portrett av mann med briller og grått hår. Foto.")
      figcaption Jan Erik Vold
    figure.m1rem
      img(src="./Postmodernisme_Eldrid-Lunden.jpg" alt="Smilande kvinne held blomebukett. Foto.")
      figcaption Eldrid Lunden
    figure.m1rem
      img(src="./Postmodernisme_Einar-Okland.jpg" alt="Portrett av mann med briller. Foto.")
      figcaption Einar Økland
    figure.m1rem
      img(src="./Postmodernisme_Paal-Helge-Haugen.jpg" alt="Portrett av mann med grå bart og hår. Foto.")
      figcaption Paal Helge Haugen

  NdlaAudio(
    source="media/periods/postmodernisme/Vold_trikkeskinnediktetdiktet.mp3"
    description="Jan Erik Vold: Trikkeskinnediktet"
    )
    blockquote
      strong «Trikkeskinnediktet»
      br
      br
      | Tenkte jeg skulle skrive et dikt
      br
      | om trikkeskinner.
      br
      | Engang begynte jeg på ett: Byen ligger bundet
      br
      | i sitt nett av trikkeskinner
      br
      | – kom ikke lenger.
      br
      | Gå ut og se på trikkeskinnene, de binder ikke
      br
      | byen fast, de ligger nedfelt i gaten
      br
      | med brostein solidarisk på begge sider, gå ut
      br
      | og se på byen, den er ikke bundet, byer
      br
      | kan ikke bindes. Nei, trikkeskinner
      br
      | er til å legge femøringer på,
      br
      | trikken paraderte
      br
      | på brune mynter opp Theresegate, vi stod i portrom
      br
      | parat til å stikke av om trikken
      br
      | stanset, konduktøren ut! Flatklemte
      br
      | femøringer, store som engelske
      br
      | pennystykker (verdt 8 1/3 øre), gjerne
      br
      | klemt skjeve, ingen elg
      br
      | på femørene dengang men gode gamle hå syv. Trikkeskinner
      br
      | og sykling! en gang gikk jeg på hue
      br
      | foran læreren, det var i svingen ved Sporveisgata
      br
      | utenfor parfymeriet og lekebutikken der, bare med ballongdekk
      br
      | var man trygg (hadde
      br
      | halvballong). Om våren kommer
      br
      | den siste snøen seilende i klumper
      br
      | ned trikkeskinnene, skyver brunt vann foran seg
      br
      | med fine bølgestriper i. Siden blå menn
      br
      | som skyfler skinnene rene så solen
      br
      | skal få klatre uten sand i munnen
      br
      | opp bakken til Adamstua, der leira ligger våt
      br
      | og snart skal bli gras
      br
      | mellom skinnene.       
  

  .mt40
  h5 1970-åra: Sosialrealistisk litteratur
  p I 1973 vart eit nytt, radikalt parti stifta: Arbeidaranes Kommunistparti (marxist-leninistane) – forkorta AKP (m-l). <strong>Dag Solstad</strong> (1941– ), <strong>Espen Haavardsholm</strong> (1945– ) og <strong>Tor Obrestad</strong> (1938–2020) var blant dei Profil-forfattarane som vart med i AKP. No byrja dei å skrive såkalla sosialrealistiske romanar. Litteraturen skulle tene det arbeidande folket og opprøret mot kapitalismen.

  .flex.portretter
    figure.m1rem
      img(src="./Postmodernisme_Dag-Solstad.jpg" alt="Halvnært portrett av mann med bustete hår og briller. Foto.")
      figcaption Dag Solstad
    figure.m1rem
      img(src="./Postmodernisme_Espen-Haavardsholm.jpg" alt="Portrett av smilande mann med grått skjegg og rutete skjorte. Foto.")
      figcaption Espen Haavardsholm
    figure.m1rem
      img(src="./Postmodernisme_Tor-Obrestad.jpg" alt="Portrett av mann med grått hår. Foto.")
      figcaption Tor Obrestad

  figure.col.col6.right-image
    img(src="./Postmodernisme_Litteraturen12.jpg" alt="Demonstrasjonstog for kvinnefrigjering. Foto.")
    figcaption Kvinnefronten i 8. marstog i 1973. Foto: Svein Erik Dahl / Samfoto / Scanpix

  h5 Nye kjønnsroller og sosiale endringar
  p Det feministiske engasjementet i 1970-åra førte til radikale endringar i familielivet og til fleire kvinner i utdanning og arbeid. Frå 1970-åra får vi derfor ei bølgje av romanar som tematiserer kjønnsrollene og dei sosiale endringane som følgde av kvinnekampen. 

  p Forfattarar som <strong>Bjørg Vik</strong> (1935–2018), <strong>Tove Nilsen</strong> (1952– ), <strong>Anne Karin Elstad</strong> (1938–2012) og <strong>Herbjørg Wassmo</strong> (1942– ) skriv alle om korleis kvinner opplever dei nye rollene dei har fått, og om forventningar knytte til desse rollene. Også dei kvinnelege prosaforfattarane i 1970-åra brukte stort sett eit tradisjonelt, realistisk formspråk. 

  .flex.portretter.portretter-5
    figure.m1rem
      img(src="./Postmodernisme_Bjorg-Vik.jpg" alt="Portrett av kvinne med oppsatt hår. Foto.")
      figcaption Bjørg Vik
    figure.m1rem
      img(src="./Postmodernisme_Tove-Nilsen.jpg" alt="Portrett av glad kvinne med blondt hår og hjartesmykke. Foto.")
      figcaption Tove Nilsen
    figure.m1rem
      img(src="./Postmodernisme_Anne-Karin-Elstad.jpg" alt="Portrett av kvinne med halvlangt, kvitt hår. Foto.")
      figcaption Anne Karin Elstad
    figure.m1rem
      img(src="./Postmodernisme_Herbjorg-Wassmo.jpg" alt="Portrett av glad kvinne med mørkt, halvlangt hår. Foto.")
      figcaption Herbjørg Wassmo
    figure.m1rem
      img(src="./Postmodernisme_Kjartan-Flogstad.jpg" alt="Portrett av mann med bart. Han ser ned. Foto.")
      figcaption Kjartan Fløgstad

  h5 Kjartan Fløgstad (1944– ) 
  p er ein annan sentral forfattar som debuterte i slutten av 1960-åra. Fløgstad har ofte industriarbeidarar og sjømenn som hovudpersonar i romanane sine. Han skriv eksperimentelt og leikande, romanane hans er inspirerte av modernistiske uttrykksformer og av den latinamerikanske magiske realismen. 

  h5 Lyrikken i 1970-åra
  p Også lyrikken i 1970-åra er prega av politikk og samfunnsengasjement, og kamp- og brukslyrikken får ei ny blomstringstid. I kampen for eit betre og meir rettferdig samfunn blir det på nytt meir akseptert å skrive dikt med fast rytme og enderim. 

  figure
    img(src="./Postmodernisme_Litteraturen18.jpg" alt="Fire musikarar som opptrer i stue. Foto.")
    figcaption Visegruppa Ballade øver til ein turné i 1978. Frå venstre: Lillebjørn Nilsen, Lars Klevstrand, Åse Kleveland og Birgitte Grimstad. Foto: Paul Owesen / NTB Scanpix

  p Det veks òg fram nye arenaer for formidling av dikt. <strong>Lillebjørn Nilsen</strong> (1950– ), <strong>Kari Svendsen</strong> (1950– ) og <strong>Lars Klevstrand</strong> (1949– ) er eksempel på visesongarar som framførte eigne og andre sine tekstar, gjerne tonesette dikt – i kampen for fred og jamstilling og mot kapitalismen eller krigen i Vietnam. Også arbeidardikting og kvardagspoesi vart formidla gjennom song. Den populære venstreradikale gruppa Vømmøl gav ut fleire LP-ar med omfattande tekstvedlegg. 

  .flex.portretter
    figure.m1rem
      img(src="./Postmodernisme_Lillebjorn-Nilsen.jpg" alt="Portrett av mann med langt hår, åpen skjorte med skjerf i halsen. Foto.")
      figcaption Lillebjørn Nilsen
    figure.m1rem
      img(src="./Postmodernisme_Kari-Svendsen.jpg" alt="Sprudlande kvinne opptrer med banjo på scenen. Foto.")
      figcaption Kari Svendsen
    figure.m1rem
      img(src="./Postmodernisme_Lars-Klevstrand.jpg" alt="Portrett av mann i sorte klede utendørs. Foto.")
      figcaption Lars Klevstrand

  NrkVideo(id="74741")

  p Dei norske visesongarane henta inspirasjon frå USA og andre engelsktalande land. Framståande artistar som <strong>Bob Dylan</strong> (1941– ), <strong>Joni Mitchell</strong> (1943– ) og <strong>Neil Young</strong> (1945– ) påverka ein heil generasjon gjennom songane sine. 

  p Den politiske kampen i 1970-åra var også nasjonal i sin karakter. Kampen mot EEC, dagens EU, førte til ei interesse for bygda og det som var særmerkt norsk. Dette syner seg i at folkeliv over heile landet vart brukt som motiv i tekstar som gjerne var skrivne på dialekt. 

  h3 Litteraturen i 1980-åra

  h5 Nostalgi og sjarmerande outsiderar 
  figure.col.col4.right-image
    img(src="./Postmodernisme_Lars-Saabye-Christensen.jpg" alt="Skalla mann med skjegg poserar med handa bak hovudet. Foto.")
    figcaption Lars Saabye Christensen

  p <strong>Lars Saabye Christensen</strong> (1953– ) er ein av dei forfattarane som har selt mest i nyare tid. Frå 1980-åra har Saabye Christensen lykkast i å skrive kritikarrost litteratur for den breie massen. Romanar som 
    em Beatles 
    | (1984), 
    em Herman 
    | (1988) og 
    em Halvbroren 
    | (2001) har nesten ikonisk status for generasjonen som var ungdom i 60-åra. Det er ofte ein outsiderkarakter vi følgjer i romanane – ein karakter som blir skildra med varme og humor. 

  .clear 
  .mt20
  h5 Rus, vald og psykiatri 
  figure.col.col4.right-image
    img(src="./Postmodernisme_Ingvar-Ambjornsen.jpg" alt="Portrett av langhåra mann med briller. Foto.")
    figcaption Ingvar Ambjørnsen

  p <strong>Ingvar Ambjørnsen</strong> (1956– ) er ein annan forfattar som har skrive om outsiderar, og som tradisjonelt har hatt ein sterk posisjon i skulen. Romanen 
    em Hvite niggere 
    | (1986) er forteljinga om nokre kameratar som blir dregne meir og meir inn i eit rusmiljø. Tittelen siktar til at hovudpersonane oppfattar seg sjølve som utstøytte individ i samfunnet. Ambjørnsen er òg svært kritisk til tilbodet som psykiatriske pasientar får i Noreg, og gjennom bøkene sine om Elling skildrar han varmt og menneskeleg livet til ein psykiatrisk pasient. 

  NrkVideo(id="3244")

  h5 Lyrikken på 1980-talet 
  p I 1980-åra debuterer <strong>Øyvind Berg</strong> (1959– ), <strong>Rune Christiansen</strong> (1963– ) og <strong>Gro Dahle</strong> (1962– ). Samfunnsengasjementet blir tona ned eller endrar karakter både hos nye og etablerte forfattarar, og det opnar rom for meir sentrallyriske motiv.

  .flex.portretter.portretter-4
    figure.m1rem
      img(src="./Postmodernisme_Oyvind-Berg.jpg" alt="Portrett av smilande mann med briller. Foto.")
      figcaption Øyvind Berg
    figure.m1rem
      img(src="./Postmodernisme_Rune-Christiansen.jpg" alt="Portrett av mann med høyhalsa genser. Foto.")
      figcaption Rune Christiansen
    figure.m1rem
      img(src="./Postmodernisme_Gro-Dahle.jpg" alt="Portrett av kvinne med musefletter som held hendene foran munnen. Foto.")
      figcaption Gro Dahle
    figure.m1rem
      img(src="./Postmodernisme_Triztan-Vindtorn.jpg" alt="Portrett av mann med langt lyst hår. Foto.")
      figcaption Triztán Vindtorn

  p Ein forfattar som debuterte i 70-åra, men som først slo gjennom i 80-åra, er <strong>Triztán Vindtorn</strong> (1942–2009). Han er den mest reindyrka surrealisten i norsk lyrikk, og skreiv eksperimentelle dikt der han bryt dei fleste språklege konvensjonane. 

  h3 Litteraturen 1990–2010
  figure.col.col6.left-image
    img(src="./Postmodernisme_Litteraturen28.jpg" alt="Kvinne i kjole opptrer på scene. Mannleg kollega i bakgrunnen. Foto.")
    figcaption Ei scene frå 
      em Rambuku 
      | av Jon Fosse på Det Norske Teatret, med Ragnhild Hilt og Svein  Erik Brodal. Foto: Carl Martin Nordby / Scanpix

  h5 Språkleik og sjangerblanding
  p Litteraturen i tiåret før og etter tusenårsskiftet er omfattande og mangslungen. Tida vil vise kva som er mest karakteristisk for vår tid, men éi endring er tydeleg etter 1985: 
    em Postmodernismen 
    | er komen til Noreg. Forfattarane eksperimenterer med det litterære formspråket, dei blandar sjangrar, leiker med språkformer og stilar og kombinerer ”fint” og folkeleg. Dessutan låner dei frå andre tekstar og andre forfattarar, eit trekk vi kallar intertekstualitet. 

  p Postmoderne tekstar er ofte 
    em metafiksjonale
    | . Med det meiner vi at forfattaren bruker grep som viskar ut grensene mellom røyndom og fiksjon. Forfattaren kan for eksempel kommentere skriveprosessen sin i forteljinga, eller han kan sjølv medverke som figur der. 

  p I kva grad kan språket skildre røyndomen? Og korleis kan det poetiske språket fange og skildre andre sider ved røyndomen enn den likeframme, realistiske framstillingsmåten? Er det i det heile nokon vits i å prøve? Slike spørsmål fengslar elles svært ulike forfattarar som 
    strong Kjartan Fløgstad
    | , 
    strong Jon Fosse 
    | (1959– ) og 
    strong Cecilie Løveid (1951– ). 

  p I Noreg er det få forfattarar som omtaler seg sjølve som postmoderne, men vi finn eksempel på lyrikk inspirert av postmodernismen hos så ulike forfattarar som for eksempel <strong>Jan Erik Vold</strong> og <strong>Gro Dahle</strong>. 

  figure.col.col6.left-image
    img(src="./Postmodernisme_Litteraturen29.jpg" alt="Statister i militære kostymer ved kulisser til ei sceneoppsetning. Foto.")
    figcaption Frå innspelinga av filmen «Sofies verden», som byggjer på Jostein Gaarders roman. Her er det ei scene frå den franske revolusjonen som blir spela inn. Foto: Rolf Øhman / Scanpix


  h5 Kommersielle suksessar
  p I 1990- og 2000-åra peiker <strong>Anne B. Ragde</strong> (1957– ) og <strong>Tore Renberg</strong> (1972– ) seg ut som to tradisjonelle forfattarar med stor suksess – Ragde med sin romantrilogi om Torunn som arvar ein gard etter ein far ho aldri har kjent, og Renberg med sin serie om Jarle Klepp, ei humoristisk skildring av korleis det var å vere ung i 1990-åra.

  p I tillegg kjem <strong>Jostein Gaarders</strong> (1952– ) filosofiske ungdomsroman 
    em Sofies verden 
    | (1991), som er omsett til 54 språk og har selt i over 30 millionar eksemplar. 

  .flex.portretter
    figure.m1rem
      img(src="./Postmodernisme_Anne-B.-Ragde.jpg" alt="Portrett av kvinne med kort, sort hår. Foto.")
      figcaption Anne B. Ragde
    figure.m1rem
      img(src="./Postmodernisme_Tore-Renberg.jpg" alt="Halvnært portrett av mann med kort hår. Foto.")
      figcaption Tore Renberg
    figure.m1rem
      img(src="./Postmodernisme_Jostein-Gaarder.jpg" alt="Portrett av mann med kort skjegg. Foto.")
      figcaption Jostein Gaarder


  ImageTextBlock.mt30(
    :image="require(`./Postmodernisme_Litteraturen33.jpg`)"
    altText="Nærbilete av mann med gitar i bakgrunnen. Foto."    
    )
    h5 Jan Kjærstad 
    p (1953– ) blir rekna for ein av dei mest sentrale postmoderne forfattarane i norsk litteratur. I sine romanar frå 1980- og 1990-åra eksperimenterer han med form, forteljeteknikk, komposisjon og karakterbygging. 

  ImageTextBlock(
    :image="require(`./Postmodernisme_Litteraturen34.jpg`)"
    altText="Portrett av mann med handa på haka. Foto."    
    )
    h5 Jon Fosse 
    p (1959– ) blir gjerne kalla ”den nye Ibsen”. Frå 1990-åra har Fosse hatt stor internasjonal suksess med sine drama, og til liks med Ibsen er teaterteknikken hans nyskapande og utforskande. Fosses forfattarskap er allereie av stort omfang – han har gitt ut mange verk og har òg skrive romanar og lyrikk.

  h5 Familien i ei postmoderne tid
  p <strong>Hanne Ørstavik</strong> (1969– ) og <strong>Trude Marstein</strong> (1973– ) tematiserer kor gale det kan gå når menneska ikkje evnar å skape gode relasjonar til kvarandre. I eit samfunn der vi sjølve må finne meining i tilværet, blir dei nære relasjonane ekstra viktige. Heimen og familien er dessutan ein trygg base som vi treng i ei verd der miljøøydeleggingar, naturkatastrofar og krig råkar verda over. Når familien sviktar, blir konsekvensane derfor katastrofale for karakterane i bøkene til Marstein og Ørstavik. 

  .flex.portretter.portretter-5
    figure.m1rem
      img(src="./Postmodernisme_Hanne-Orstavik.jpg" alt="Portrett av smilande kvinne med kort, kvitt hår. Foto.")
      figcaption Hanne Ørstavik
    figure.m1rem
      img(src="./Postmodernisme_Trude-Marstein.jpg" alt="Portrett av kvinne med handa på kinnet. Foto.")
      figcaption Trude Marstein
    figure.m1rem
      img(src="./Postmodernisme_Lars-Ramslie.jpg" alt="Halvnært portrett av mann som bøyer hovudet litt og ser opp. Foto.")
      figcaption Lars Ramslie
    figure.m1rem
      img(src="./Postmodernisme_Erlend-Loe.jpg" alt="Portrett av skalla mann med rutete skjorte. Foto. ")
      figcaption Erlend Loe
    figure.m1rem
      img(src="./Postmodernisme_Helene-Uri.jpg" alt="Portrett av smilande kvinne med langt mellomblondt hår. Foto.")
      figcaption Helene Uri

  NrkVideo(id="3412")

  figure.col.col4.left-image
    img(src="./Postmodernisme_Bilde-fra-Tatt-av-kvinnen.jpg" alt="Mann med ryggsekk sit ved sidan av ei kvinne under eit laken på scenen. Foto.")
    figcaption Gard B. Eidsvold og Helle Haugen i ei scene frå Centralteatret si oppsetjing av teaterstykket «Tatt av kvinnen», som byggjer på romanen til Erlend Loe. Foto: Erlend Aas / Scanpix

  h5 Den postmoderne mannen – ”tatt av kvinnen” 
  p Også i den postmoderne litteraturen er kjønnsroller eit gjennomgangstema, og det postmoderne perspektivet er ofte mannens perspektiv. Mennene har mist evna til å ta initiativ overfor kvinna – dei veit ikkje lenger kva kvinner vil ha. Dette ser vi særleg i 
    em Fatso 
    | (2003) av <strong>Lars Ramslie</strong> (1974– ) og i 
    em Tatt av kvinnen 
    | (1993) av <strong>Erlend Loe</strong> (1969– ). Dei mannlege karakterane er her tafatte og einsame og heilt prisgitt ei aktiv kvinne. 

  p I 
    em Dyp rød 315 
    | (2001) av <strong>Helene Uri</strong> (1964– ) møter vi ei kvinne som reduserer mannen til eit objekt og ein gjenstand på lik linje med skjerf og hanskar. Den kvinnelege hovudkarakteren Gina Beck solar seg i glansen av ein mann som ho trær på armen som ei ny veske. Og når denne mannen ikkje lenger tilfredsstiller behova hennes, byter ho han ut. 

  NrkVideo(id="3246")

  figure.col.col4.right-image
    img(src="./Postmodernisme_Litteraturen41.jpg" alt="Kvinneleg forfattar på bokbad. Foto. ")
    figcaption Åsne Seierstad på Litteraturhuset i 2008. Foto: Knut Falch / Scanpix

  h5 Nye problemstillingar
  p Dei store konfliktane i litteraturen i dag handlar ikkje lenger om form, men om grensene mellom fiksjon og sakprosa og om dei verdivala forfattarane gjer. I kor stor grad kan ein basere seg på verkelege personar og hendingar når ein skriv skjønnlitteratur? Kva er skilnaden på ein roman og ei dokumentarbok? 

  figure.col.col4.left-image
    img(src="./Postmodernisme_Litteraturen42.jpg" alt="Mann med kort hår, skjeggstubbar og skinnjakke. Foto. ")
    figcaption Karl Ove Knausgård. Foto: Tore Meek / Scanpix

  p Desse spørsmåla har vorte særleg aktuelle etter dommen mot <strong>Åsne Seierstad</strong> (1970– ) i 2010. Seierstad vart dømd til å betale Shah Mohammad Rais erstatning for å ha basert romanen 
    em Bokhandleren i Kabul 
    | (2002) på livet til Rais og hans familie. Også det veldige seksbindsverket til <strong>Karl Ove Knausgård</strong> (1968– ), 
    em Min Kamp 
    | (2009–2011), synleggjer etiske dilemma ved å skrive så tett opp til eige liv og røynsle: Kva skjer når ekskoner, nære familiemedlemmer og ein avdød far blir romankarakterar? 

  NrkVideo.mt20(id="5980")

  h3 Språket i perioden

  figure
    img(src="./Postmodernisme_Spraaket_i_perioden1.jpg" alt="Mann får gave mens fleire menneske ser på. Foto.")
    figcaption Elevar frå Oddernes gymnas overleverte i 1975 ca. 25 000 underskrifter til stortingspresident Guttorm Hansen (til høgre) for å be Stortinget om å ta sidemålsstilen opp til revisjon. Men sidemålsundervisninga i skulen står støtt også i dag. Foto: Erik Berglund / Aftenposten / Scanpix

  h5 Språkfred
  p Mens språksituasjonen i Noreg i 1950-åra var prega av konflikt og massiv motstand mot samnorskpolitikken, såg vi ved inngangen til 1960-åra teikn til mildvêr i språkstriden. Då ein komité – populært kalla språkfredskomiteen – vart nedsett i 1963, hadde han ikkje mandat til å lage forslag til ei ny rettskrivingsreform. Komiteen skulle kome med framlegg til korleis vi kunne ta vare på og utvikle den norske språkarven. Samnorsktanken var for omstridd til at dei politiske partia ønskte å gå vidare med dette arbeidet. 

  p I tida etter 1960 stabiliserte språksituasjonen i Noreg seg i det todelte landskapet som vi også har i dag. Bokmål er den heilt klart dominerande målforma, mens nynorsk har ei oppslutning på ikring 15 prosent. Nynorsk står sterkast på Vestlandet, men har òg ein sterk posisjon innanfor litteratur og utdanning. I skulen er det framleis obligatorisk sidemålsundervisning, og i somme humanistiske fag på universiteta står nynorsk også sterkt. 

  p I offentleg forvaltning krevst det at dei tilsette meistrar begge målformene. Styresmaktene har klare retningslinjer for kor mykje av offentleg kommunikasjon og publikasjonar som skal vere på bokmål og på nynorsk. I NRK er det krav om 25 prosent nynorsk skrift og normert tale. Bokmål er nærast einerådande på område som populærkultur, økonomi og teknologi. 

  h3 Andre kunstformer

  figure
    img(src="./Postmodernisme_Andre_kunstformer1.jpg" alt="Kunstverk av sovande barn i ramme. Foto. ")
    figcaption Odd Nerdrum: «Baby» (1993). Foto: Bjørn Reese / VG / Scanpix

  h5 Biletkunst
  figure.col.col4.left-image
    img(src="./Postmodernisme_Campbells-Soup.jpg" alt="Eit par ser på vegg med mange like plakatar av Campell hermetikkboks. Foto.")
    figcaption Andy Warhol: «Campbell’s Soup»

  p Då <strong>Andy Warhol</strong> (1928–1987) i 1960-åra stilte ut bilete av Campbell tomatsuppe, vart publikum nøydde til å tenkje over kva kunst eigentleg er. Warhol tok kjende produkt og gjenstandar ut av sin vanlege samanheng og gjorde dei til kunst. På den måten vart han ein banebrytar for postmoderne kunstretningar der ideen bak kunstverket var viktigare enn sjølve produktet. 

  figure.col.col4.left-image
    img(src="./Postmodernisme_Pushwagner_.jpg" alt="Eldre mann med solbriller og dress foran tre av kunstverka sine. Foto.")
    figcaption Pushwagner (Terje Brofos)

  p Den norske kunstnaren <strong>Terje Brofos</strong> (1940–2018), kjend under namnet <strong>Hariton Pushwagner</strong>, gjer bruk av formspråket i popkunsten. Som den amerikanske ”popart”-kunstnaren <strong>Roy Lichtenstein</strong> (1923–1997) nyttar Pushwagner teikneserien, ein tradisjonell lågkultursjanger, som utgangspunkt. Han er mellom anna kjend for bilete som viser det uoversiktlege bylivet og det framandgjorde gjennomsnittsmennesket. 

  p Her i landet markerer <strong>Odd Nerdrum</strong> (1944– ) seg som ein av dei mest sentrale postmoderne kunstnarane. I motsetning til andre samtidige kunstnarar har Nerdrum valt eit formspråk som liknar barokkmålarane på 1600-talet. Men motiva hans er henta frå samtida, og på den måten blandar Nerdrum nytt og gammalt. Nerdrum kallar sjølv bileta sine kitsch, for å markere at han tek avstand frå det etablerte kunstsynet i samtida. 

  figure.col.col4.right-image
    img.col.col12(src="./Postmodernisme_Marianne-Heske.jpg" alt="Kvinne sit i ein kunstinstallasjon med mange små glassfigurar. Foto.")
    figcaption Marianne Heske på ei utstilling på Henie Onstad Kunstsenter. Foto: Mimsy Møller / Samfoto / NTB

  p Ein annan sentral norsk kunstnar i den postmoderne tradisjonen er <strong>Marianne Heske</strong> (1946– ). Marianne Heske tingar produkt av lokale handverkarar, som ho så set saman til installasjonar, bilete eller skulpturar. Ein slik konseptuell kunst – der ideen er viktigare enn sjølve handverket – er typisk for den postmoderne retninga. 

  figure.col.col4.left-image
    img(src="./Postmodernisme_Kjartan-Slettemark.jpg" alt="Mann utkledd i kunstnerisk kostyme. Foto.")
    figcaption Kjartan Slettemark

  p <strong>Kjartan Slettemark</strong> (1932–2008) er ein annan sentral kunstnar i denne perioden. Slettemarks kunst er både sterkt politisk, eksperimentell og leikande i forma. Slettemark er òg kjend for å eksperimentere med ulike medium i kunsten og for sin performancekunst. 

  .clear
  h5 Arkitektur 
  figure.col.col5.right-image
    img(src="./Postmodernisme_Andre_kunstformer6.jpg" alt="Fasade på bygg med mykje glass. Foto.")
    figcaption Aker Brygge
  p <strong>Aker Brygge</strong> i Oslo kan stå som symbol på postmoderne arkitektur i Noreg. Fram til 1982 var Aker Brygge ein travel industristad der Akers mekaniske Verksted bygde skip. Etter at denne verksemda vart nedlagd, gjorde ein om heile industriområdet til ein stad for underhaldning, forretningar og bustader for pengesterke menneske. Aker Brygge kan dermed symbolisere overgangen frå eit industrisamfunn til eit kommersielt mediesamfunn. 

  p Arkitekturen er også typisk for den postmoderne estetikken – arkitektane har nemleg integrert dei gamle industrihallane i dei moderne bygningane som vart oppførte. Denne blandinga av ny og gammal arkitektur er eit typisk postmoderne trekk. 

  h5 Musikk 
  p Frå 1960-åra og fram til vår tid har rock og pop vore dei klart dominerande musikksjangrane. Det musikalske uttrykket spenner vidt – frå lett diskomusikk i 1970-åra til opprørsk punk i 1980-åra og glatte ”boyband” i 1990-åra. Felles er evna til å formidle kjensler, anten det er snakk om kjærleikssorg, politisk urett eller gleda over å vere ung. Saman med filmen har pop- og rockemusikken ein eineståande identitetsskapande posisjon hos ungdom som annan litteratur og kunst aldri har hatt. 

  figure
    img(src="./Postmodernisme_Stones.jpg" alt="Mannleg vokalist på scenen foran stort publikum på ein fotballstadion. Foto.")
    figcaption Mick Jagger i aksjon under ein Rolling Stones-konsert.


  p I 1960-åra var musikken knytt til ungdomsopprøret i samtida, og grupper som <strong>Beatles</strong> og <strong>Rolling Stones</strong> vart saman med <strong>Bob Dylan</strong> (1941– ) viktige symbol for ungdomane den gong. I 70-åra tok den litt meir svevande og drøymande hippiekulturen over, og artistar som <strong>Janis Joplin</strong> (1943–1970), <strong>Jimi Hendrix</strong> (1942–1970) og <strong>Jim Morrison</strong> (1943–1971, med gruppa Doors) var viktige stemmer her. 

  figure
    img(src="./Postmodernisme_Aha.jpg" alt="Musikkgruppe framfører låt på scene. Foto.")
    figcaption Foto: Knut Snare / Aftenposten / NTB
  

  p Bandet <strong>a-ha</strong> er utan tvil den største internasjonale norske musikksuksessen i perioden etter 1960. A-ha greidde med ”Take on me” noko ingen norske band har greidd verken før eller seinare – dei selde til førsteplass på den amerikanske ”billboard”-lista. 

  ImageTextBlock(
    :image="require(`./Postmodernisme_Andre_kunstformer10.jpg`)"
    altText="Mannleg saksofonist på scene. Foto."   
    imageCaption="Jan Garbarek. Foto: Knut Fjeldstad / Scanpix" 
    )
    h5 Jan Garbarek 
    p (1947– ) er ein av få norske musikarar med internasjonal aksept som stilskapar innanfor jazzen. Garbarek er kjend for å blande inn folketonar i jazzen, og for sitt samarbeid med poeten Jan Erik Vold. Album som 
      em Briskeby Blues 
      | (1969) og 
      em Ingentings bjeller 
      | (1977) nådde eit breitt publikum. 

  ImageTextBlock(
    :image="require(`./Postmodernisme_Andre_kunstformer11.jpg`)"
    altText="Kvinneleg vokalist på scene. Foto."
    imageCaption="Mari Boine. Foto: Mimsy Møller / Samfoto / Scanpix"  
    )
    h5 Mari Boine 
    p (1956– ) er ein annan norsk artist med internasjonal suksess. Musikken hennar er sterkt inspirert av samiske folketonar og joik. Ho bruker jazz og rock til å fornye og modernisere den samiske musikken – ein musikk som ofte blir omtalt som ”verdsmusikk”. 

  h5 Massemedium 
  p Etter at NRK vart offisielt opna i 1960,voks tv-mediet fort. Satellittar gjorde det tidleg mogleg å få inn heile verda i stova, og i 1969 kunne vi følgje sjølve storhendinga i hundreåret: Dei første stega på månen vart direkte overførte på tv.

  p Fram til 1982 hadde NRK monopol på å sende fjernsyns- og radioprogram, men så vart det opna opp for andre aktørar. Både lokale kringkastarar og internasjonale satellittoverførte kanalar skapte konkurranse. 

  figure
    img(src="./Postmodernisme_Gerd-Liv-Valla.jpg" alt="Kvinne foran mange mikrofonar på ein pressekonferanse. Foto.")
    figcaption I dag finst det eit mangfald av tv- og radiokanalar som formidlar nyheiter til norske sjåarar og lyttarar. Biletet er teke i 2007 og viser ein pressekonferanse med dåverande LO-leiar Gerd-Liv Valla. 

  p Eit typisk trekk ved moderne mediehistorie er 
    em kommersialisering
    | . Eit ledd i kommersialiseringa av media er meir tabloidisering og kjendisdyrking. Avisene VG og Dagbladet og vekebladet Se og Hør representerer på ulike måtar presse som bruker kjendis- og livsstilsstoff for å nå ut til eit større publikum. Men også tv og radio sel kjendisstoff – og dei skaper kjendisar. 

  p I dag er mediebiletet prega av 
    em globalisering
    | . Store delar av verda er no berre eit tastetrykk eller ein av-og-på-knapp unna. Særleg har Internett sett fart i denne globaliseringstendensen. Både Internett og mobiltelefonen fekk sitt gjennombrot i midten av 1990-åra, og dei har sidan skapt store endringar i kvardagen til folk. 

  p Eit anna typisk trekk ved media etter 2005 er den auka bruken av sosiale medium, for eksempel bloggar, Facebook, Twitter, YouTube og Instagram. Vi ser òg tendensar til at desse media påverkar tradisjonelle medium, og denne utviklinga vil vi sikkert sjå meir av i framtida. 

</template>

<script>
import ImageTextBlock from "@/components/ImageTextBlock";
import PagePeriod from "@/components/PagePeriod";
import NdlaVideo from "@/components/NdlaVideo";
import NdlaAudio from "@/components/NdlaAudio";
import NrkVideo from "@/components/NrkVideo";

export default {
  name: "PostmodernismeNN",
  components: {
    ImageTextBlock,
    NdlaAudio,
    NdlaVideo,
    NrkVideo,
    PagePeriod,
  },
};
</script>
